import {
  UNDATED_PATRIOT_BACKFILL_LANES,
  type PatriotBackfillControl,
  type PatriotBackfillLane,
  type PatriotBackfillScan,
  type PatriotBackfillScanName,
  type StartPatriotBackfillRequest,
} from "@visioni/a3s-monitoring/app";
import type { DayRangeValues } from "./components/ActivationDateRangeField";

/** What each lane copies, in the words support would use for it. */
export const PATRIOT_BACKFILL_LANE_LABELS: Record<PatriotBackfillLane, string> = {
  activations: "Activations",
  signals: "Signals",
  operatorNotes: "Operator notes",
  clients: "Clients",
  actionPlans: "Action plans",
  clientGroupingTypes: "Grouping types",
  clientGroupings: "Groupings",
};

/** What each of a backfill's passes copies. */
export const PATRIOT_BACKFILL_SCAN_LABELS: Record<PatriotBackfillScanName, string> = {
  activationsCreated: "Activations created",
  activationsCompleted: "Activations completed",
  activationsOpen: "Open activations",
  signals: "Signals",
  operatorNotes: "Operator notes",
  clients: "Clients",
  actionPlans: "Action plans",
  clientGroupingTypes: "Grouping types",
  clientGroupings: "Groupings",
};

/** One line of a backfill's progress: a pass, and where it has got. */
export interface BackfillScanLine {
  readonly scan: PatriotBackfillScanName;
  readonly label: string;
  readonly text: string;
  readonly failed: boolean;
}

/**
 * A line per pass of a backfill, in the order the run fetches them (the order
 * the API lists them in): running or paused (with how
 * far through its range a dated pass is), retrying, done, stopped, or failed
 * and why.
 * `start` is the run's range start, where every dated pass begins. Empty for a
 * run from before passes were recorded.
 */
export function backfillScanLines(
  run: { readonly scans: readonly PatriotBackfillScan[]; readonly start?: string | undefined },
  formatMoment: (iso: string) => string,
): BackfillScanLine[] {
  return run.scans.map((scan) => ({
    scan: scan.scan,
    label: PATRIOT_BACKFILL_SCAN_LABELS[scan.scan],
    text: scanText(scan, run.start, formatMoment),
    failed: scan.status === "failed",
  }));
}

function scanText(scan: PatriotBackfillScan, start: string | undefined, formatMoment: (iso: string) => string): string {
  const counted = `${scan.enumerated.toLocaleString()} checked, ${scan.emitted.toLocaleString()} queued`;
  if (scan.status === "done") return `Done · ${counted}`;
  if (scan.status === "failed") {
    const at = scan.upTo === undefined ? "" : ` at ${formatMoment(scan.upTo)}`;
    return `Failed${at}${scan.lastError === undefined ? "" : `: ${scan.lastError}`}`;
  }
  if (scan.status === "stopped") return `Stopped · ${progressText(scan, counted, start, formatMoment)}`;
  if (scan.status === "paused") return `Paused · ${progressText(scan, counted, start, formatMoment)}`;
  if (scan.failures > 0) {
    return `Retrying (attempt ${String(scan.failures + 1)})${scan.lastError === undefined ? "" : `: ${scan.lastError}`}`;
  }
  return `Running · ${progressText(scan, scan.until === undefined ? `${counted} so far` : counted, start, formatMoment)}`;
}

/** What a pass has counted and, for a dated one, how far through its range it is. */
function progressText(
  scan: PatriotBackfillScan,
  counted: string,
  start: string | undefined,
  formatMoment: (iso: string) => string,
): string {
  if (scan.until === undefined) return counted;
  const percent = rangePercent(start, scan.upTo, scan.until);
  return scan.upTo === undefined ?
      `${counted} · ${String(percent)}%`
    : `${counted} · up to ${formatMoment(scan.upTo)} (${String(percent)}%)`;
}

/**
 * What can be done to a backfill now: pause while a pass is running, resume
 * once one is paused, and stop either way. Nothing once every pass is over.
 */
export function backfillRunControls(run: { readonly scans: readonly PatriotBackfillScan[] }): PatriotBackfillControl[] {
  const running = run.scans.some((scan) => scan.status === "running");
  const paused = run.scans.some((scan) => scan.status === "paused");
  const controls: PatriotBackfillControl[] = [];
  if (running) controls.push("pause");
  if (paused) controls.push("resume");
  if (running || paused) controls.push("stop");
  return controls;
}

/** How far `upTo` is through `[start, until)`, as a whole percentage; 0 before the first hour is done. */
function rangePercent(start: string | undefined, upTo: string | undefined, until: string): number {
  if (start === undefined || upTo === undefined) return 0;
  const span = Date.parse(until) - Date.parse(start);
  if (!(span > 0)) return 0;
  return Math.min(100, Math.max(0, Math.floor(((Date.parse(upTo) - Date.parse(start)) / span) * 100)));
}

export interface BackfillRangeFields {
  readonly days: DayRangeValues;
  /** `HH:mm` on the first day; blank is midnight. */
  readonly fromTime: string;
  /** `HH:mm` on the last day; blank is the end of that day. */
  readonly toTime: string;
}

export interface BackfillRangeResult {
  /** Absent until both days are chosen and every field reads. */
  readonly range?: { start: string; end: string };
  readonly errors: { fromTime?: string; toTime?: string; range?: string };
}

/** Whether every lane copies all its records, so the range plays no part. */
export const isUndatedOnly = (lanes: readonly PatriotBackfillLane[]): boolean =>
  lanes.length > 0 && lanes.every((lane) => UNDATED_PATRIOT_BACKFILL_LANES.includes(lane));

export interface BackfillRequestFields extends BackfillRangeFields {
  readonly lanes: readonly PatriotBackfillLane[];
  readonly force: boolean;
}

/**
 * The backfill to start for the form, once it has a lane and, if any lane is
 * dated, a range. Undated lanes ignore the range, so with only those chosen
 * the days and times play no part.
 */
export function backfillRequest({ lanes, force, ...rangeFields }: BackfillRequestFields): {
  readonly request?: StartPatriotBackfillRequest;
  readonly errors: BackfillRangeResult["errors"];
} {
  const [firstLane, ...otherLanes] = lanes;
  if (firstLane === undefined) return { errors: {} };
  const named: StartPatriotBackfillRequest = { lanes: [firstLane, ...otherLanes], force };
  if (isUndatedOnly(lanes)) return { request: named, errors: {} };

  const { range, errors } = backfillRange(rangeFields);
  return range === undefined ? { errors } : { request: { ...named, ...range }, errors };
}

const TIME_ERROR = "Use a 24-hour time, like 18:00";

/**
 * The backfill range for the form's days and times, in the browser's
 * timezone. With no times it covers both days whole; a time narrows its own
 * end, down to a window of minutes within one day.
 */
export function backfillRange(fields: BackfillRangeFields): BackfillRangeResult {
  const fromTime = parseTime(fields.fromTime);
  const toTime = parseTime(fields.toTime);
  const errors = {
    ...(fromTime === "invalid" && { fromTime: TIME_ERROR }),
    ...(toTime === "invalid" && { toTime: TIME_ERROR }),
  };
  if (fromTime === "invalid" || toTime === "invalid") return { errors };

  const start = localTime(fields.days.from, fromTime ?? { hours: 0, minutes: 0 });
  // A blank end time is midnight after the last day, so that day counts whole.
  const end = localTime(fields.days.to, toTime ?? { hours: 24, minutes: 0 });
  if (start === undefined || end === undefined) return { errors };
  if (start >= end) return { errors: { range: "The range has to end after it starts" } };
  return { range: { start: start.toISOString(), end: end.toISOString() }, errors };
}

/** `undefined` for a blank field, `"invalid"` for one that isn't a 24-hour `H:mm`/`HH:mm`. */
function parseTime(text: string): { hours: number; minutes: number } | "invalid" | undefined {
  const trimmed = text.trim();
  if (trimmed === "") return undefined;
  const match = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
  const hours = Number(match?.[1]);
  const minutes = Number(match?.[2]);
  if (!match || hours > 23 || minutes > 59) return "invalid";
  return { hours, minutes };
}

function localTime(day: string | undefined, time: { hours: number; minutes: number }): Date | undefined {
  if (day === undefined) return undefined;
  const [y, m, d] = day.split("-").map(Number);
  if (y === undefined || m === undefined || d === undefined) return undefined;
  return new Date(y, m - 1, d, time.hours, time.minutes);
}

/** One line of what a backfill is about to do, for the check before it starts. */
export interface BackfillSummaryLine {
  readonly label: string;
  readonly value: string;
}

/**
 * What a backfill will copy, for confirming before it starts: the time-based
 * records and the range they cover, the records copied whole, and whether
 * every record is copied again.
 */
export function backfillSummary(
  request: StartPatriotBackfillRequest,
  formatMoment: (iso: string) => string,
): BackfillSummaryLine[] {
  const labels = (lanes: readonly PatriotBackfillLane[]) =>
    lanes.map((lane) => PATRIOT_BACKFILL_LANE_LABELS[lane]).join(", ");
  const dated = request.lanes.filter((lane) => !UNDATED_PATRIOT_BACKFILL_LANES.includes(lane));
  const undated = request.lanes.filter((lane) => UNDATED_PATRIOT_BACKFILL_LANES.includes(lane));
  return [
    ...(dated.length > 0 ? [{ label: "Time-based records", value: labels(dated) }] : []),
    ...(dated.length > 0 && request.start !== undefined && request.end !== undefined ?
      [
        { label: "From", value: formatMoment(request.start) },
        { label: "To", value: formatMoment(request.end) },
      ]
    : []),
    ...(undated.length > 0 ? [{ label: "Records", value: `${labels(undated)} (all of them)` }] : []),
    { label: "Copy every record again", value: request.force ? "Yes" : "No" },
  ];
}
